import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  CreditCardIcon, 
  LockClosedIcon, 
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { usePayment } from '../context/PaymentContext';
import toast from 'react-hot-toast';
import clsx from 'clsx';

interface CheckoutFormData {
  // Customer Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company?: string;
  
  // Billing Address
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  
  // Payment Information
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  nameOnCard: string;
  
  // Terms
  agreeToTerms: boolean;
  subscribeToUpdates: boolean;
}

interface EventSummary {
  id: string;
  title: string;
  date: string;
  location: string;
  price: number;
}

const CheckoutPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { state: paymentState, processPayment } = usePayment();
  const [event, setEvent] = useState<EventSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Get sponsor selection data from navigation state
  const sponsorData = location.state as {
    selectionType?: string;
    sponsorTier?: any;
    additionalSponsorships?: string[];
    addGolfers?: boolean | null;
    planType?: string;
  } | null;

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<CheckoutFormData>({
    defaultValues: {
      country: 'US',
      subscribeToUpdates: true
    }
  });

  // Mock event data - Replace with API call
  const mockEvents: Record<string, EventSummary> = useMemo(() => ({
    '1': {
      id: '1',
      title: 'Advanced Collision Repair Workshop',
      date: '2026-05-15',
      location: 'Chicago, IL',
      price: 299
    },
    '2': {
      id: '2',
      title: 'Paint Technology Seminar',
      date: '2026-05-22',
      location: 'Dallas, TX',
      price: 199
    }
  }), []);

  useEffect(() => {
    // Handle sponsor registration (no eventId needed)
    if (sponsorData?.selectionType === 'sponsor') {
      setLoading(false);
      return;
    }
    
    // Handle regular event registration
    if (eventId) {
      setTimeout(() => {
        const eventData = mockEvents[eventId];
        if (eventData) {
          setEvent(eventData);
        }
        setLoading(false);
      }, 500);
    } else {
      // No eventId and no sponsor data - redirect to events page
      setLoading(false);
    }
  }, [eventId, sponsorData, mockEvents]);

  const formatCardNumber = (value: string) => {
    // Remove all non-digit characters
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    
    // Add spaces every 4 digits
    const matches = v.match(/\d{4,16}/g);
    const match = matches?.[0] || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (xxx) xxx-xxxx
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return phoneNumber;
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('phone', formatted);
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setValue('cardNumber', formatted);
  };

  const calculateTotal = () => {
    if (!event) return 0;
    const subtotal = event.price;
    const processingFee = Math.round(subtotal * 0.029); // 2.9% processing fee
    const tax = Math.round(subtotal * 0.08); // 8% tax (example)
    return subtotal + processingFee + tax;
  };

  const onSubmit = async (data: CheckoutFormData) => {
    if (!event) return;

    setSubmitting(true);

    try {
      // Prepare customer data
      const customerData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        company: data.company,
        address: {
          street: data.address,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          country: data.country
        }
      };

      // Prepare payment data for Xplor API
      const paymentData = {
        amount: calculateTotal(),
        currency: 'USD',
        paymentMethod: 'card',
        card: {
          number: data.cardNumber.replace(/\s/g, ''), // Remove spaces
          expiry: `${data.expiryMonth}/${data.expiryYear}`,
          cvv: data.cvv,
          name: data.nameOnCard
        },
        description: `Event Registration: ${event.title}`,
        metadata: {
          eventId: event.id,
          eventTitle: event.title,
          eventDate: event.date
        }
      };

      // Process payment through Xplor API
      await processPayment(event.id, customerData, paymentData);

      if (paymentState.success) {
        toast.success('Payment successful! Registration confirmed.');
        navigate('/confirmation');
      }

    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!event && !sponsorData?.selectionType) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Registration Not Found</h1>
          <p className="text-gray-600 mb-8">Please select a registration option to continue.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            Go Back to Registration
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {sponsorData?.selectionType === 'sponsor' ? 'Complete Your Sponsorship' : 'Complete Your Registration'}
            </h1>
            <p className="text-lg text-gray-600">
              Secure checkout powered by Xplor payment processing
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="lg:grid lg:grid-cols-3 lg:gap-12">
            {/* Form Content */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Selected Tier Summary (STAT 2) */}
              {sponsorData?.selectionType === 'sponsor' && sponsorData?.sponsorTier && (
                <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-2">
                        SELECTED TIER
                      </h3>
                      <h2 className="text-xl font-bold text-gray-900 mb-1">
                        {sponsorData.sponsorTier.name}
                      </h2>
                      <p className="text-sm text-gray-600 mb-1">
                        {sponsorData.sponsorTier.description} — <span className="text-green-600 font-medium">Saves ${sponsorData.sponsorTier.savings.toLocaleString()}</span>
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="text-sm text-red-600 hover:text-red-800 underline"
                      >
                        Change tier
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-red-600">
                        ${sponsorData.sponsorTier.price.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Primary Contact Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">PRIMARY CONTACT</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      {...register('firstName', { required: 'First name is required' })}
                      className={clsx(
                        'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.firstName && 'border-red-500'
                      )}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      {...register('lastName', { required: 'Last name is required' })}
                      className={clsx(
                        'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.lastName && 'border-red-500'
                      )}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address'
                        }
                      })}
                      className={clsx(
                        'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.email && 'border-red-500'
                      )}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      placeholder="(123) 456-7890"
                      {...register('phone', { 
                        required: 'Phone number is required',
                        pattern: {
                          value: /^\(\d{3}\) \d{3}-\d{4}$/,
                          message: 'Please enter a valid phone number'
                        }
                      })}
                      onChange={handlePhoneNumberChange}
                      maxLength={14}
                      className={clsx(
                        'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.phone && 'border-red-500'
                      )}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company (Optional)
                    </label>
                    <input
                      type="text"
                      {...register('company')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-6">
                  <CreditCardIcon className="w-6 h-6 text-primary-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Payment Information</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Number *
                    </label>
                    <input
                      type="text"
                      maxLength={19}
                      placeholder="1234 5678 9012 3456"
                      {...register('cardNumber', { 
                        required: 'Card number is required',
                        minLength: {
                          value: 19,
                          message: 'Please enter a valid card number'
                        }
                      })}
                      onChange={handleCardNumberChange}
                      className={clsx(
                        'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.cardNumber && 'border-red-500'
                      )}
                    />
                    {errors.cardNumber && (
                      <p className="text-red-500 text-sm mt-1">{errors.cardNumber.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Month *
                      </label>
                      <select
                        {...register('expiryMonth', { required: 'Month is required' })}
                        className={clsx(
                          'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                          errors.expiryMonth && 'border-red-500'
                        )}
                      >
                        <option value="">MM</option>
                        {Array.from({ length: 12 }, (_, i) => {
                          const month = (i + 1).toString().padStart(2, '0');
                          return (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year *
                      </label>
                      <select
                        {...register('expiryYear', { required: 'Year is required' })}
                        className={clsx(
                          'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                          errors.expiryYear && 'border-red-500'
                        )}
                      >
                        <option value="">YYYY</option>
                        {Array.from({ length: 10 }, (_, i) => {
                          const year = new Date().getFullYear() + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CVV *
                      </label>
                      <input
                        type="text"
                        maxLength={4}
                        placeholder="123"
                        {...register('cvv', { 
                          required: 'CVV is required',
                          minLength: {
                            value: 3,
                            message: 'CVV must be at least 3 digits'
                          }
                        })}
                        className={clsx(
                          'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                          errors.cvv && 'border-red-500'
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name on Card *
                    </label>
                    <input
                      type="text"
                      {...register('nameOnCard', { required: 'Name on card is required' })}
                      className={clsx(
                        'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.nameOnCard && 'border-red-500'
                      )}
                    />
                    {errors.nameOnCard && (
                      <p className="text-red-500 text-sm mt-1">{errors.nameOnCard.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      {...register('agreeToTerms', { required: 'You must agree to the terms' })}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label className="ml-3 text-sm text-gray-700">
                      I agree to the{' '}
                      <a href="/terms" className="text-primary-600 hover:text-primary-700">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" className="text-primary-600 hover:text-primary-700">
                        Privacy Policy
                      </a> *
                    </label>
                  </div>
                  {errors.agreeToTerms && (
                    <p className="text-red-500 text-sm">{errors.agreeToTerms.message}</p>
                  )}

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      {...register('subscribeToUpdates')}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label className="ml-3 text-sm text-gray-700">
                      Subscribe to event updates and industry news
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      {event ? (
                        <>
                          <h3 className="font-medium text-gray-900">{event.title}</h3>
                          <p className="text-sm text-gray-600">
                            {new Date(event.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-sm text-gray-600">{event.location}</p>
                        </>
                      ) : sponsorData?.sponsorTier ? (
                        <>
                          <h3 className="font-medium text-gray-900">{sponsorData.sponsorTier.name}</h3>
                          <p className="text-sm text-gray-600">
                            Crash Champions Classic Golf Tournament 2026
                          </p>
                          <p className="text-sm text-gray-600">TPC Summerlin, Las Vegas</p>
                          {sponsorData.addGolfers && (
                            <p className="text-sm text-green-600 font-medium mt-2">
                              ✓ Includes FREE golfers (saves ${sponsorData.sponsorTier.savings.toLocaleString()})
                            </p>
                          )}
                        </>
                      ) : (
                        <h3 className="font-medium text-gray-900">Registration</h3>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-gray-200 pt-4 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="text-gray-900">
                        ${event ? event.price : sponsorData?.sponsorTier ? sponsorData.sponsorTier.price.toLocaleString() : 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Processing Fee</span>
                      <span className="text-gray-900">
                        ${Math.round((event ? event.price : sponsorData?.sponsorTier ? sponsorData.sponsorTier.price : 0) * 0.029).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax</span>
                      <span className="text-gray-900">
                        ${Math.round((event ? event.price : sponsorData?.sponsorTier ? sponsorData.sponsorTier.price : 0) * 0.08).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-3 font-semibold text-lg">
                      <span className="text-gray-900">Total</span>
                      <span className="text-gray-900">${calculateTotal().toLocaleString()}</span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || paymentState.isProcessing}
                    className={clsx(
                      'w-full px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center',
                      submitting || paymentState.isProcessing
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    )}
                  >
                    {submitting || paymentState.isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <LockClosedIcon className="w-5 h-5 mr-2" />
                        Complete Registration
                      </>
                    )}
                  </button>

                  {paymentState.error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center">
                        <ExclamationCircleIcon className="w-5 h-5 text-red-600 mr-2" />
                        <span className="text-sm text-red-800">{paymentState.error}</span>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-center text-xs text-gray-500">
                    <LockClosedIcon className="w-4 h-4 mr-1" />
                    Secured by Xplor Payment Processing
                  </div>
                </div>
              </div>
            </div>
          </form>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;